import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import useAppLayout from './hooks/useAppLayout';
import API_URL from './api/config';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ReactModal from 'react-modal';
import { formatDistanceToNow, parseISO } from 'date-fns';
import imageCompression from 'browser-image-compression';
import ImageWithLoader2 from './components/ImageWithLoader2';

const facebookStyles = `
  :root{
    --space-2: 8px;
    --space-3: 12px;
    --space-4: 16px;

    /* your brand greens */
    --g1: #75B437;
    --g2: #184A2C;
  }

  .fb-container{ background:#f0f2f5; min-height:100vh; }

  .feed-wrap{
    width:100%;
    max-width:680px;
    margin:0 auto;
    padding: clamp(10px,2.5vw,16px) clamp(8px,2vw,14px);
  }

  /* ===== COMPOSER (Create Post) ===== */
  .fb-create-post{
    background:#fff; border-radius:12px;
    box-shadow:0 1px 2px rgba(0,0,0,.08);
    padding: 12px;
    margin-bottom: 14px;
  }
  .fb-composer-row{ gap: 10px; }
  .fb-avatar-lg{ width:40px; height:40px; border-radius:50%; object-fit:cover; background:#e9ecef; display:flex; align-items:center; justify-content:center; }

  .fb-post-input{
    background:#f0f2f5; border:none; border-radius:12px;
    padding: 10px 12px;
    font-size:14px; line-height: 20px;
    min-height: 40px; max-height: 160px;
    resize: none; overflow: hidden;
    word-break: break-word; white-space: pre-wrap;
  }
  .fb-post-input:focus{ background:#e4e6ea; outline:none; box-shadow:none; }

  .fb-action-row{ margin-top: 8px; }
  .fb-action-btn{
    background:none; border:none; padding:6px 10px;
    border-radius:6px; color:#65676b; font-weight:500;
  }
  .fb-action-btn:hover{ background:#f0f2f5; }

  /* ====== Gradient buttons ====== */
  .btn-post{
    --grad: linear-gradient(135deg, var(--g1) 0%, var(--g2) 100%);
    background: var(--grad);
    border:none;
    padding:8px 16px;
    border-radius:999px;
    font-size:14px; color:#fff; font-weight:600;
    box-shadow: 0 6px 18px rgba(24,74,44,.22);
    transition: transform .06s ease, filter .2s ease, box-shadow .2s ease;
  }
  .btn-post:hover{ filter: brightness(1.05); box-shadow: 0 8px 22px rgba(24,74,44,.28); }
  .btn-post:active{ transform: scale(.98); }
  .btn-post:disabled{ opacity:.65; cursor:not-allowed; box-shadow:none; }

  /* Override Bootstrap primary to use the same gradient (modals, comment Post button) */
  .btn-primary{
    --grad: linear-gradient(135deg, var(--g1) 0%, var(--g2) 100%);
    background: var(--grad) !important;
    border: none !important;
    color: #fff !important;
    box-shadow: 0 6px 18px rgba(24,74,44,.18);
  }
  .btn-primary:hover{ filter: brightness(1.05); }
  .btn-primary:disabled{ opacity:.65; box-shadow:none; }

  /* Outline primary with gradient hover */
  .btn-outline-primary{
    color: var(--g2) !important;
    border: 1px solid var(--g1) !important;
    background: transparent !important;
  }
  .btn-outline-primary:hover{
    color:#fff !important;
    background: linear-gradient(135deg, var(--g1) 0%, var(--g2) 100%) !important;
    border-color: transparent !important;
  }

  /* ===== POSTS ===== */
  .fb-post-card{
    background:#fff; border:none; border-radius:10px;
    box-shadow:0 1px 2px rgba(0,0,0,.08);
    margin-bottom: 14px; overflow:hidden;
  }

  .fb-avatar{ width:36px; height:36px; border-radius:50%; object-fit:cover; background:#e9ecef; }
  .fb-username{ font-weight:600; color:#050505; text-decoration:none; }
  .fb-username:hover{ text-decoration:underline; }
  .fb-timestamp{ color:#65676b; font-size:12px; }

  .fb-post-text{
    font-size:15px; line-height:1.33; color:#050505; margin: 6px 0 6px;
    word-break: break-word; white-space: pre-wrap;
  }

  .fb-like-btn{ background:none; border:none; padding:6px 10px; border-radius:6px; font-weight:500; color:#65676b; }
  .fb-like-btn.liked{ color: var(--g2); }
  .fb-like-btn:hover{ background:#f0f2f5; }
  .fb-post-actions{ border-top:1px solid #e4e6ea; padding-top:6px; }

  .fb-comment-section{ background:#f7f8fa; padding:10px 12px; }
  .fb-comment-input{ background:#f0f2f5; border:none; border-radius:16px; padding:8px 10px; font-size:14px; }
  .fb-comment-input:focus{ background:#e4e6ea; outline:none; }

  /* Media frame for all aspect ratios (portrait/landscape/square) */
  .fb-media-frame{
    position:relative; width:100%;
    background:#000; display:flex; align-items:center; justify-content:center;
    border-radius: 8px; overflow:hidden;
  }
  .fb-post-img{
    display:block; max-width:100%; max-height:72vh;
    width:auto; height:auto; object-fit:contain;
    background:transparent;
  }

  /* tiny phones */
  @media (max-width:420px){
    .fb-avatar, .fb-avatar-lg{ width:34px; height:34px; }
    .fb-create-post{ padding:10px; }
    .fb-post-input{ min-height:38px; font-size:13px; }
    .btn-post{ padding:6px 12px; font-size:13px; }
    .fb-post-card .p-3{ padding:10px !important; }
  }


  /* ReactModal overlay + content */
.rm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.45);
  z-index: 2000;             /* above your header/footer */
}

.rm-content {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: min(92vw, 720px);   /* responsive */
  max-height: 85vh;
  overflow: auto;
  border: none;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 24px 60px rgba(0,0,0,.25);
  padding: 0;                /* let .modal-content handle padding if you keep it */
}

/* prevent background scroll when a modal is open */
body.ReactModal__Body--open {
  overflow: hidden;
}

`;



function FamilyPosts() {
  const { menu, toggleMobileMenu } = useAppLayout();

  const loggedInUserId = localStorage.getItem("user_id");
  const loggedFamilyAdmin = localStorage.getItem("family_admin");

  const queryClient = useQueryClient();
  const limit = 10;

  const fetchPosts = async (page) => {
    const offset = page * limit;
    const res = await axios.get(`${API_URL}/get-status-posts-api?limit=${limit}&offset=${offset}`, {
      withCredentials: true,
    });
    return res.data;
  };



  const fetchComments = async (statusId) => {
    const res = await axios.get(`${API_URL}/get-status-comments-api/${statusId}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    if (res.data.success) {
      return res.data.data;
     
    } else {
      throw new Error('Failed to fetch comments');
    }
  };

  const [postList, setPostList] = useState([]);
  const [page, setPage] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [replyMessages, setReplyMessages] = useState({});
  const [openPosts, setOpenPosts] = useState({});
  const [commentData, setCommentData] = useState({});
  const [commentLoading, setCommentLoading] = useState({});
  const [commentError, setCommentError] = useState({});

  const {
    data: statusPostData,
    isLoadingPosts,
    isErrorPosts,
    refetch,
  } = useQuery({
    queryKey: ['statusPosts', page],
    queryFn: () => fetchPosts(page),
    staleTime: 0,
    cacheTime: Infinity,
    keepPreviousData: true,
  });


  useEffect(() => {
    if (statusPostData?.success) {
      setPostList((prev) => {
        const newPosts = page === 0 ? statusPostData.data : [...prev, ...statusPostData.data];
        const uniquePosts = newPosts.filter((post, index, self) =>
          index === self.findIndex((p) => p.id === post.id)
        );
        return uniquePosts;
      });
      setIsLoadingMore(false);
    }
  }, [statusPostData, page]);

  const loadMore = () => {
    setIsLoadingMore(true);
    setPage((prevPage) => prevPage + 1);
  };


  const handleReplyChange = (statusId, message) => {
    setReplyMessages((prev) => ({ ...prev, [statusId]: message }));
  };
  

  //post status

const [fileName, setFileName] = useState('');

  const [postText, setPostText] = useState('');
  const [image, setImage] = useState(null);

  const [isPosting, setIsPosting] = useState(false); // for button disable

const handlePostStatus = async () => {
  // Check that at least one of text or image is provided
  if (!postText.trim() && !image) {
    toast.error('Please enter text or select an image to share.');
    return;
  }

  setIsPosting(true); // disable button

  try {
    let compressedImage = image;

    // Compress image if one is selected
    if (image) {
      const targetKB = 100;
      let quality = 0.9;

      while (true) {
        const options = {
          maxSizeMB: targetKB / 1024, // ~200KB
          initialQuality: quality,
          useWebWorker: true,
        };

        compressedImage = await imageCompression(image, options);

        if (compressedImage.size / 1024 <= targetKB || quality <= 0.1) break;
        quality -= 0.1; // Keep reducing quality until target size reached
      }
    }

    // Build FormData
    const formData = new FormData();
    formData.append('postText', postText);
    if (compressedImage) {
      formData.append('postImage', compressedImage);
    }

    const response = await axios.post(`${API_URL}/save-status-posts-api`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });

    if (response.data.success) {
      toast.success('Post shared successfully!');
      setPostText('');
      setImage(null);
      setFileName('');
      queryClient.invalidateQueries(['statusPosts']);
    } else {
      toast.error(response.data.message || 'Something went wrong.');
    }
  } catch (err) {
    console.error('Error posting status:', err);
    toast.error('Failed to post. Please try again.');
  } finally {
    setIsPosting(false); // Re-enable button
  }
};

  
  //end post status

  //post comments

  const handlePostComment = async (statusId) => {
    const replyMessage = replyMessages[statusId];
    if (!replyMessage?.trim()) {
      toast.error('Reply cannot be empty.');
      return;
    }
    try {
      const response = await axios.post(`${API_URL}/reply-status-posts-api`, {
        status_id: statusId,
        replymessage: replyMessage,
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (response.data.success) {
        toast.success('Replied to post');
        setReplyMessages((prev) => ({ ...prev, [statusId]: '' }));
        loadComments(statusId); // Refresh comments after posting
      } else {
        toast.error(response.data.message || 'Failed to reply.');
      }
    } catch (error) {
      console.error('Reply error:', error);
      toast.error('Could not post comment. Try again.');
    }
  };

////end post comments

  const loadComments = async (statusId) => {
    setCommentLoading((prev) => ({ ...prev, [statusId]: true }));
    setCommentError((prev) => ({ ...prev, [statusId]: false }));
    try {
      const data = await fetchComments(statusId);
      setCommentData((prev) => ({ ...prev, [statusId]: data }));
    } catch (error) {
      setCommentError((prev) => ({ ...prev, [statusId]: true }));
    } finally {
      setCommentLoading((prev) => ({ ...prev, [statusId]: false }));
    }
  };


  const toggleComments = (statusId) => {
    setOpenPosts((prev) => {
      const isOpen = !prev[statusId];
      if (isOpen && !commentData[statusId]) {
        loadComments(statusId);
      }
      return { ...prev, [statusId]: isOpen };
    });
  };

  //modal edit post




  const [editPostModalOpen, setEditPostModalOpen] = useState(false);
const [editingPost, setEditingPost] = useState(null);
const [editPostText, setEditPostText] = useState('');
const [editPostImage, setEditPostImage] = useState(null);


const [deletePostModalOpen, setDeletePostModalOpen] = useState(false);
const [deletingPost, setDeletingPost] = useState(null);
  //edit posts and comments

  const [isUpdatingPost, setIsUpdatingPost] = useState(false); // for disabling the button

  const handleUpdatePost = async () => {
    if (!editingPost) return;
  
    setIsUpdatingPost(true); // Disable button while updating
  
    try {
      let compressedImage = editPostImage;
  
      // Compress image if present
      if (editPostImage) {
        const targetKB = 100;
        let quality = 0.9;
  
        while (true) {
          const options = {
            maxSizeMB: targetKB / 1024, // ~200KB
            initialQuality: quality,
            useWebWorker: true,
          };
  
          compressedImage = await imageCompression(editPostImage, options);
  
          if (compressedImage.size / 1024 <= targetKB || quality <= 0.1) break;
          quality -= 0.1;
        }
      }
  
      const formData = new FormData();
      formData.append('status_id', editingPost.id);
      formData.append('postText', editPostText);
      if (compressedImage) {
        formData.append('postImage', compressedImage);
      }
  
      const response = await axios.post(`${API_URL}/update-status-post-api`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
  
      if (response.data.success) {
        toast.success('Post updated!');
        setEditPostModalOpen(false);
        setEditingPost(null);
        queryClient.invalidateQueries(['statusPosts']);
      } else {
        toast.error('Update failed.');
      }
    } catch (err) {
      console.error('Update error:', err);
      toast.error('Something went wrong.');
    } finally {
      setIsUpdatingPost(false); // Re-enable button
    }
  };


  const [viewImageModalOpen, setViewImageModalOpen] = useState(false);
const [viewImageSrc, setViewImageSrc] = useState(null);


  const [reportPostModalOpen, setReportPostModalOpen] = useState(false);
  const [reportingPost, setReportingPost] = useState(null);
  const [reportPostText, setReportPostText] = useState('');
  const [reportReason, setReportReason] = useState('');
  const [isReportingPost, setIsReportingPost] = useState(false);


  const handleReportPost = async () => {
    if (!reportReason.trim()) return;
  
    try {
      setIsReportingPost(true);
  
      await axios.post(`${API_URL}/report-status-post-api`, {
        postId: reportingPost.id,
        reason: reportReason
      });
  
      toast.success('Post reported successfully');
      setReportPostModalOpen(false);
      setReportingPost(null);
  
    } catch (err) {
      console.error(err);
      toast.error('Failed to report post');
    } finally {
      setIsReportingPost(false);
    }
  };

 


const handleDeletePost = async () => {
  if (!deletingPost) return;

  try {
    const response = await axios.post(`${API_URL}/delete-status-post-api`, {
      status_id: deletingPost.id,
    }, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });

    if (response.data.success) {
      toast.success('Post deleted!');
      setDeletePostModalOpen(false);
      setDeletingPost(null);
      queryClient.invalidateQueries(['statusPosts']);
    } else {
      toast.error('Delete failed.');
    }
  } catch (err) {
    console.error(err);
    toast.error('Something went wrong.');
  }
};



const [editingComment, setEditingComment] = useState(null);
const [editingCommentText, setEditingCommentText] = useState('');
const [deleteCommentModalOpen, setDeleteCommentModalOpen] = useState(false);
const [commentToDelete, setCommentToDelete] = useState(null);


const handleEditComment = async () => {
  if (!editingCommentText.trim()) {
    toast.error('Comment cannot be empty.');
    return;
  }

  try {
    const response = await axios.post(`${API_URL}/edit-status-comment-api`, {
      comment_id: editingComment.id, // use original id
      new_comment: editingCommentText,
    }, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });

    if (response.data.success) {
      toast.success('Comment updated');
      setEditingComment(null);
      setEditingCommentText('');
      await loadComments(editingComment.status_id);
    } else {
      toast.error('Failed to update comment.');
    }
  } catch (err) {
    console.error('Edit comment error:', err);
    toast.error('Could not update comment.');
  }
};


const handleDeleteComment = async () => {
  if (!commentToDelete) return;

  try {
    const response = await axios.post(`${API_URL}/delete-status-comment-api`, {
      comment_id: commentToDelete.id,
    }, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });

    if (response.data.success) {
      toast.success('Comment deleted');
      setDeleteCommentModalOpen(false);
      await loadComments(commentToDelete.status_id); // ✅ this is the fix
    } else {
      toast.error('Failed to delete comment.');
    }
  } catch (err) {
    console.error('Delete comment error:', err);
    toast.error('Could not delete comment.');
  }
};


//likes
const [likeStatus, setLikeStatus] = useState({});

const fetchLikeInfo = async (statusId) => {
  const res = await axios.get(`${API_URL}/get-status-likes-count-api/${statusId}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  });
  if (res.data.success) {
    return res.data;
  }
  throw new Error('Failed to fetch like info');
};




const toggleLike = async (statusId) => {
  const prev = likeStatus[statusId] || { liked: false, likeCount: 0 };
  const newLikeStatus = {
    liked: !prev.liked,
    likeCount: prev.liked ? prev.likeCount - 1 : prev.likeCount + 1
  };

  //Immediately update UI for responsiveness
  setLikeStatus(prevState => ({
    ...prevState,
    [statusId]: newLikeStatus
  }));

  try {
    const res = await axios.post(`${API_URL}/like-status-post-api`, {
      status_id: statusId,
    }, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });

    // Optionally, re-fetch from server in case server has different logic
    if (res.data.success) {
      const updated = await fetchLikeInfo(statusId);
      setLikeStatus(prevState => ({
        ...prevState,
        [statusId]: updated
      }));
    }
  } catch (err) {
    console.error('Like error:', err);
    //Optional: Rollback UI if request failed
    setLikeStatus(prevState => ({
      ...prevState,
      [statusId]: prev
    }));
  }
};


useEffect(() => {
  if (statusPostData?.success) {
    const newPosts = page === 0 ? statusPostData.data : [...postList, ...statusPostData.data];
    const uniquePosts = newPosts.filter((post, index, self) =>
      index === self.findIndex((p) => p.id === post.id)
    );
    setPostList(uniquePosts);
    setIsLoadingMore(false);

    // Fetch likes for all posts only after postList is updated
    uniquePosts.forEach((post) => {
      fetchLikeInfo(post.id).then(data => {
        setLikeStatus(prev => ({ ...prev, [post.id]: data }));
      });
    });
  }
}, [statusPostData]);
//end likes



    return (
      <>
      <style>{facebookStyles}</style>
      <div className={`main-wrapper ${menu ? 'slide-nav': ''}`}>
      <Header onMenuClick={(value) => toggleMobileMenu()} />
      <Sidebar />
        <ToastContainer />
         <div className="page-wrapper">
         <div className="content container-fluid">

         <div className="row justify-content-center">
         <div className="col-md-7">
          
          
          
              <div className="fb-create-post">
                              <div className="d-flex align-items-center fb-composer-row mb-2">
                                {/* Logged-in user's profile next to the text box */}
                                
                                <div className="flex-grow-1">
                                  <textarea
                                    
                                    className="form-control fb-post-input"
                                    rows={1}
                                    placeholder="What's on your mind?"
                                    value={postText}
                                      onChange={(e) => setPostText(e.target.value)}
                                  />
                                </div>
                              </div>
              
                              {fileName && (
                                <div className="mb-1">
                                  <small className="text-muted">📎 {fileName}</small>
                                </div>
                              )}
              
                              <div className="d-flex justify-content-between align-items-center fb-action-row">
                              <label
                className="fb-action-btn d-flex align-items-center justify-content-center"
                title="Add Photo"
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #75B437 0%, #184A2C 100%)',
                  color: '#fff',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(24,74,44,.25)',
                  transition: 'transform .1s ease, box-shadow .2s ease',
                }}
                onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.95)')}
                onMouseUp={(e) => (e.currentTarget.style.transform = 'scale(1)')}
              >
                <i className="fas fa-paperclip"></i>
                <input
                  type="file"
                  className="d-none"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                
                    try {
                      const options = {
                        maxSizeMB: 1,            // Max size (MB)
                        maxWidthOrHeight: 800,   // Resize max dimension
                        useWebWorker: true,
                      };
                      
                      const compressedFile = await imageCompression(file, options);
                      setImage(compressedFile);
                      setFileName(file.name);
                    } catch (err) {
                      console.error("Image compression error:", err);
                      toast.error("Failed to compress image.");
                    }
                  }}
                />
              </label>
              
              
                                <button
                                  onClick={handlePostStatus}
                                  className="btn btn-post"
                                  disabled={isPosting}
                                >
                                  {isPosting ? 'Posting...' : 'Post'}
                                </button>
                              </div>
                            </div>
          
          
            </div>
          </div>
          
          
          
          
                      <div className="row justify-content-center">
                        <div className="col-md-7">
                        {isLoadingPosts && postList.length === 0 ? (
                            
                            <div className='loadingspinnerDiv'>
                              <div className='loadingspinner'></div>
                            </div>
          
                          ) : (
                            <div>
                          {postList.map(post => (
                            <div key={post.id} className="card fb-post-card">
                              <div className="card-body p-0">
          
          
                              <div className="d-flex justify-content-between align-items-start p-3">
                                  <div className="d-flex align-items-center">
                                    {post.user?.user_image ? (
                                      <ImageWithLoader2
                                        src={`${API_URL}/images/${post.user.user_image}`}
                                        placeholder="https://oret.africa/tree/img/imageplaceholder-icon.jpg"
                                        className="fb-avatar me-2"
                                        alt="User"
                                      />
                                    ) : (
                                      <div className="fb-avatar me-2 d-flex align-items-center justify-content-center fw-bold text-muted">
                                        {(post.user?.name?.[0] || 'U').toUpperCase()}
                                      </div>
                                    )}
                                    <div>
                                      <Link to="#" className="fb-username">
                                        {post.user?.n_prefix} {post.user?.name} {post.user?.last_name} {post.user?.surname}
                                      </Link>
                                      <div
                                        className="fb-timestamp" >
                                       {/* {formatDistanceToNow(parseISO(post.date_posted), { addSuffix: true })}*/}

                                       {post?.date_posted &&
 !isNaN(new Date(post.date_posted)) ? 
  formatDistanceToNow(new Date(post.date_posted), { addSuffix: true }) 
  : ""}
                                      </div>
                                    </div>
                                  </div>
          
                                 
                                    <div className="dropdown">
                                      <button className="btn btn-sm" data-bs-toggle="dropdown">
                                        <i className="fas fa-ellipsis-h text-muted"></i>
                                      </button>
                                      <ul className="dropdown-menu">
          
                                      {parseInt(loggedInUserId) === parseInt(post.user_id) && (
                                        <>
                                        <li>
                                          <button
                                            className="dropdown-item"
                                            onClick={() => {
                                              setEditPostModalOpen(true);
                                              setEditingPost(post);
                                              setEditPostText(post.status || '');
                                              setEditPostImage(null);
                                            }}
                                          >
                                            <i className="fas fa-edit me-3"></i>Edit Post
                                          </button>
                                        </li>
                                        <li>
                                          <button
                                            className="dropdown-item text-danger"
                                            onClick={() => {
                                              setDeletePostModalOpen(true);
                                              setDeletingPost(post);
                                            }}
                                          >
                                            <i className="fas fa-trash me-2"></i>Delete Post
                                          </button>
                                        </li>
                                        </>
                                        )}
          
          
                                    {parseInt(loggedInUserId) !== parseInt(post.user_id) && (
          
                                        <li>
          
                                        <button
                                              className="dropdown-item"
                                              onClick={() => {
                                                setReportPostModalOpen(true);
                                                setReportingPost(post);
                                                setReportPostText(post.status || '');
                                                setReportReason('');
                                              }}
                                            >
                                              <i className="fas fa-flag me-2"></i>Report Post
                                            </button>
                                        </li>
                                        )}
                                      </ul>
                                    </div>
                                  
                                </div>
                                
          
                                
                              {/**Edit post */}
                                
                              {editingPost?.id === post.id && (
                                        <ReactModal
                                          isOpen={editPostModalOpen}
                                          onRequestClose={() => setEditPostModalOpen(false)}
                                          className="Modal"
                                          overlayClassName="Overlay"
                                        >
                                          <div className="modal-header modal-header-grad">
                                            <h5 className="modal-title">Edit Post</h5>
                                            <button
                                              type="button"
                                              className="btn-close modalCloseButton"
                                              onClick={() => setEditPostModalOpen(false)}
                                            ></button>
                                          </div>
          
                                          <div className="modal-body">
          
                                            <div className='row'>
                                              <div className='col-md-12'>
                                              {post.status_image && (
                                              <div style={{height: '200px', width: '100%', overflow: 'hidden'}}>
                                                <ImageWithLoader2 src={`${API_URL}/images/${post.status_image}`} 
                                                placeholder="https://oret.africa/tree/img/imageplaceholder-icon.jpg"
                                                fadeDuration={800}
                                                blurAmount={15} 
                                                alt="Post" style={{height: '200px', width: '100%', objectFit: 'cover',}} />
                                              </div>
                                              
                                            )}
          
                                              <div className="col-auto">
                                                  <label className="uploadFile" title="Attach Image">
                                                    <i className="fa fa-paperclip"></i>
          
                                                    <input
                                                        type="file"
                                                        name="image"
                                                        accept="image/*"
                                                        onChange={async (e) => {
                                                          const file = e.target.files[0];
                                                          if (!file) return;
          
                                                          try {
                                                            // Compression options
                                                            const options = {
                                                              maxSizeMB: 1,            
                                                              maxWidthOrHeight: 800, 
                                                              useWebWorker: true,  
                                                            };
          
                                                            // Compress file
                                                            const compressedFile = await imageCompression(file, options);
          
                                                            // Update your states
                                                            setEditPostImage(compressedFile);
          
                                                            // Display the file name in a preview element
                                                            document.getElementById('fileNameEditPreview').textContent = file.name;
          
                                                          } catch (err) {
                                                            console.error('Image compression error:', err);
                                                            toast.error('Failed to compress image.');
                                                          }
                                                        }}
                                                      />
                                                  </label>
                                                </div>
          
                                                <div className="col">
                                                  <label className="selected-file-name" id="fileNameEditPreview"></label>
                                                </div>
          
          
                                              </div>
                                            </div>
          
                                            <div className='row'>
                                              <div className='col-md-12'>
                                              <textarea
                                              className="form-control mb-3"
                                              rows="4" placeholder='Edit what is on your mind' 
                                              value={editPostText}
                                              onChange={(e) => setEditPostText(e.target.value)}
                                            />
                                              </div>
                                              </div>
                                            
                                          
                                          </div>
          
                                          <div className="modal-footer">
          
                                            <button
                                                onClick={handleUpdatePost}
                                                className="btn btn-primary"
                                                disabled={isUpdatingPost}
                                              >
                                                {isUpdatingPost ? "Updating..." : "Update Post"}
                                              </button>
                                          </div>
                                        </ReactModal>
                                      )}
          
                              {/**end edit post */}
          
          
          
                              {/**report post */}
                              {reportingPost?.id === post.id && (
                                        <ReactModal
                                          isOpen={reportPostModalOpen}
                                          onRequestClose={() => setReportPostModalOpen(false)}
                                          className="Modal"
                                          overlayClassName="Overlay"
                                        >
                                          <div className="modal-header modal-header-grad">
                                            <h5 className="modal-title">Report Post</h5>
                                            <button
                                              type="button"
                                              className="btn-close modalCloseButton"
                                              onClick={() => setReportPostModalOpen(false)}
                                            ></button>
                                          </div>
          
                                          <div className="modal-body">
          
                                            {/* Original post text */}
                                            <div className="mb-3">
                                              <p className="fw-semibold">{reportPostText}</p>
                                            </div>
          
                                            {/* Post image */}
                                            {post.status_image && (
                                              <div style={{ height: '200px', overflow: 'hidden' }} className="mb-3">
                                                <ImageWithLoader2
                                                  src={`${API_URL}/images/${post.status_image}`}
                                                  placeholder="https://oret.africa/tree/img/imageplaceholder-icon.jpg"
                                                  fadeDuration={800}
                                                  blurAmount={15}
                                                  alt="Post"
                                                  style={{ height: '200px', width: '100%', objectFit: 'cover' }}
                                                />
                                              </div>
                                            )}
          
                                            {/* Report reason */}
                                            <textarea
                                              className="form-control"
                                              rows="4"
                                              placeholder="Explain why you are reporting this post"
                                              value={reportReason}
                                              onChange={(e) => setReportReason(e.target.value)}
                                            />
          
                                          </div>
          
                                          <div className="modal-footer">
                                            <button
                                              onClick={handleReportPost}
                                              className="btn btn-primary"
                                              disabled={isReportingPost || !reportReason.trim()}
                                            >
                                              {isReportingPost ? "Reporting..." : "Report Post"}
                                            </button>
                                          </div>
                                        </ReactModal>
                                      )}
                              {/**end report post */}
                                
                               {deletingPost ?.id === post.id && (
          
                                          <ReactModal
                                          isOpen={deletePostModalOpen}
                                          onRequestClose={() => setDeletePostModalOpen(false)}
                                          className="Modal"
                                          overlayClassName="Overlay"
                                        >
                                          <div className="modal-header">
                                            <h5 className="modal-title">Delete Post</h5>
                                            <button
                                              type="button"
                                              className="btn-close modalCloseButton"
                                              onClick={() => setDeletePostModalOpen(false)}
                                            ></button>
                                          </div>
          
                                          <div className="modal-body">
          
                                          <p>Are you sure you want to permanently delete this post?</p>
                                          </div>
          
                                          <div className="modal-footer">
                                            
                                            <button className="btn btn-danger" onClick={handleDeletePost}>
                                            Yes Delete
                                            </button>
                                          </div>
                                        </ReactModal>
          
                                    )}
          
                              {/**end delete post */}
          
                              <div className="px-3"
                              style={{
                                filter: post.blur === 1 ? 'blur(6px)' : 'none',
                                pointerEvents: post.blur === 1 ? 'none' : 'auto'
                              }}
                              >
                                <div className="fb-post-text">{post.status}</div>
                              </div>
          
                              {post.status_image && (
          
                              <div className="fb-media-frame" style={{ marginTop: post.status ? 6 : 2, filter: post.blur === 1 ? 'blur(20px)' : 'none',
                                cursor: post.blur === 1 ? 'default' : 'pointer'
                               }}
                              onClick={() => {
                                if (post.blur === 1) return; 
                                setViewImageSrc(`${API_URL}/images/${post.status_image}`);
                                setViewImageModalOpen(true);
                              }}
                              >
                                <ImageWithLoader2
                                  src={`${API_URL}/images/${post.status_image}`}
                                  placeholder="https://oret.africa/tree/img/imageplaceholder-icon.jpg"
                                  className="fb-post-img"
                                  alt="Post"
                                />
                              </div>
          
          
                              
                              
                            )}
          
          
          
          
          
            
          
                      {/* Stats */}
                      <div className="px-3 py-2">
                      <div className="d-flex justify-content-between text-muted small">
                        <span>{likeStatus[post.id]?.likeCount || 0} likes</span>
                        <span>{post.comment_count || 0} comments</span>
                      </div>
                    </div>
          
          
                    {/* Actions */}
                    <div className="fb-post-actions px-3 pb-2">
                                  <div className="d-flex justify-content-around">
                                    <button
                                      className={`fb-like-btn ${likeStatus[post.id]?.liked ? 'liked' : ''}`}
                                      onClick={() => toggleLike(post.id)}
                                    >
                                      <i className="fas fa-thumbs-up me-2"></i>
                                      Like
                                    </button>
          
                                    <button className="fb-action-btn" onClick={() => toggleComments(post.id)}>
                                      <i className="fas fa-comment me-2"></i>
                                      Comment
                                    </button>
          
                                    <button className="fb-action-btn">
                                      <i className="fas fa-share me-2"></i>
                                      Share
                                    </button>
                                  </div>
                                </div>
          
                                
          
                                {openPosts[post.id] && (
                                  
                                  <div className="fb-comment-section">
          
                                  <div id={`collapseExample${post.id}`} className="mt-3">
          
          
                                  <div className="d-flex mb-3">
                                    <div className="fb-avatar bg-secondary rounded-circle me-2 flex-shrink-0 d-flex align-items-center justify-content-center">
                                        
                                    </div>
                                    <div className="flex-grow-1 d-flex">
          
                                    <input
                                  type="text"
                                  className="form-control fb-comment-input me-2"
                                  placeholder="Write a comment..."
                                  value={replyMessages[post.id] || ''}
                                  onChange={(e) => handleReplyChange(post.id, e.target.value)}
                                />
                                      
                                      <button className="btn btn-sm btn-primary mb-3" onClick={() => handlePostComment(post.id)}>
                                          Reply
                                        </button>
                                    </div>
                                  </div>
          
          
                                    {commentLoading[post.id] ? (
                                      <p>Loading comments...</p>
                                    ) : commentError[post.id] ? (
                                      <p className="text-danger">Failed to load comments.</p>
                                    ) : commentData[post.id]?.length > 0 ? (
                                      commentData[post.id].map((comment, i) => (
          
                                  <div key={i} className="d-flex mb-3">
          
                                     <div key={i} className="d-flex mb-3">
                                          {comment.user?.user_image ? (
                                            <ImageWithLoader2
                                              src={`${API_URL}/images/${comment.user?.user_image}`}
                                              placeholder="https://oret.africa/tree/img/imageplaceholder-icon.jpg"
                                              className="fb-avatar me-2 flex-shrink-0"
                                              alt="User"
                                            />
                                          ) : (
                                            <div className="fb-avatar me-2 d-flex align-items-center justify-content-center fw-bold text-muted">
                                              {(comment.user?.name?.[0] || 'U').toUpperCase()}
                                            </div>
                                          )}
                                          <div className="flex-grow-1">
                                            <div className="bg-light rounded-3 px-3 py-2">
                                              <div className="d-flex justify-content-between align-items-start">
                                                <div>
                                                  <div className="fw-semibold small">
                                                    {comment.user?.n_prefix} {comment.user?.name} {comment.user?.last_name}
                                                  </div>
                                                  <div className="text-muted" style={{ fontSize: '12px' }} >
                                                     {formatDistanceToNow(parseISO(comment.date_sent), { addSuffix: true })}
                                                  </div>
                                                </div>
          
                                                {parseInt(loggedInUserId) === parseInt(comment.user_id) && (
                                                  <div className="dropdown">
                                                    <button className="btn btn-sm p-0" data-bs-toggle="dropdown">
                                                      <i className="fas fa-ellipsis-h text-muted"></i>
                                                    </button>
                                                    <ul className="dropdown-menu">
                                                      <li>
                                                        <button
                                                          className="dropdown-item"
                                                          onClick={() => {
                                                            setEditingComment({ ...comment, status_id: post.id });
                                                            setEditingCommentText(comment.comment);
                                                          }}
                                                        >
                                                          Edit
                                                        </button>
                                                      </li>
                                                      <li>
                                                        <button
                                                          className="dropdown-item text-danger"
                                                          onClick={() => {
                                                            setDeleteCommentModalOpen(true);
                                                            setCommentToDelete({ ...comment, status_id: post.id });
                                                          }}
                                                        >
                                                          Delete
                                                        </button>
                                                      </li>
                                                    </ul>
                                                  </div>
                                                )}
                                              </div>
                                              <div className="mt-1">{comment.comment}</div>
                                            </div>
                                          </div>
                                        </div>
          
                                        </div>
                                      ))
                                    ) : (
                                      <p className="text-muted">No comments yet.</p>
                                    )}
          
          
          {editingComment && (
                    <ReactModal isOpen onRequestClose={() => setEditingComment(null)} className="Modal" overlayClassName="Overlay">
                      <div className="modal-header">
                        <h5 className="modal-title">Edit Comment</h5>
                        <button type="button" className="btn-close" onClick={() => setEditingComment(null)}></button>
                      </div>
                      <div className="modal-body">
                        <textarea className="form-control" value={editingCommentText} onChange={(e) => setEditingCommentText(e.target.value)} />
                      </div>
                      <div className="modal-footer">
                        <button className="btn btn-primary" onClick={handleEditComment}>Update Comment</button>
                      </div>
                    </ReactModal>
                  )}
          
                  {deleteCommentModalOpen && (
                    <ReactModal isOpen onRequestClose={() => setDeleteCommentModalOpen(false)} className="Modal" overlayClassName="Overlay">
                      <div className="modal-header ">
                        <h5 className="modal-title ">Delete Comment</h5>
                        <button type="button" className="btn-close" onClick={() => setDeleteCommentModalOpen(false)}></button>
                      </div>
                      <div className="modal-body">
                        <p>Are you sure you want to delete this comment?</p>
                      </div>
                      <div className="modal-footer">
                        <button className="btn btn-danger" onClick={handleDeleteComment}>Yes Delete</button>
                      </div>
                    </ReactModal>
                  )}
                                  </div>
          
                                  </div>
                                  
                                )}
          
          
                                
                               
          
                              </div>
          
                                
                            </div>
          
           
          
          
                          ))}
          
                          
                          
                          
                          {statusPostData?.data?.length === limit && (
                            
                            <center>
                              <button onClick={loadMore} disabled={isLoadingMore} className="loadMorePostsbtn">
                                {isLoadingMore ? 'Loading more...' : 'Load More'}
                              </button>
                            </center>
                            
                          )}
          
                          {statusPostData?.data?.length < limit && postList.length > 0 && (
                            <p className="text-center text-muted mt-4">No more posts</p>
                          )}
          
                        </div>
          
                          )}
                          
                        </div>
                      </div>

                      <div style={{ height: 8 }} />
          
        
          
                      <ReactModal
            isOpen={viewImageModalOpen}
            onRequestClose={() => setViewImageModalOpen(false)}
            className="Modal"
            overlayClassName="Overlay"
          >
            <div className="modal-header modal-header-grad">
             
              <button
                type="button"
                className="btn-close modalCloseButton"
                onClick={() => setViewImageModalOpen(false)}
              ></button>
            </div>
          
            <div className="modal-body text-center">
              {viewImageSrc && (
                <img
                  src={viewImageSrc}
                  alt="Full View"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '75vh',
                    
                  }}
                />
              )}
            </div>
          </ReactModal>
                             

        </div>
        </div>
        
        </div>
        </>
        
    );
}

export default FamilyPosts;
